/**
 * Analytics Logger - GTM DataLayer and Facebook Pixel Event Logging
 *
 * Provides wrapper functions to:
 * 1. Push events to GTM dataLayer
 * 2. Log analytics events to Supabase for troubleshooting
 * 3. Track Facebook Pixel events (CreditDirect only)
 */

import { logger, LogCategory } from './Logger';

// Declare global types for GTM and FB
declare global {
  interface Window {
    dataLayer: GTMEvent[];
    fbq?: (...args: unknown[]) => void;
  }
}

// GTM Event structure
export interface GTMEvent {
  event: string;
  session_id?: string;
  scenario_code?: number;
  outcome_type?: string;
  brand?: string;
  step_number?: number;
  fields_completed?: number;
  field_name?: string;
  error_type?: string;
  enhanced_conversion_data?: Record<string, unknown>;
  [key: string]: unknown;
}

// Facebook Pixel event parameters
export interface FBPixelParams {
  currency?: string;
  value?: number;
  content_type?: string;
  content_ids?: string[];
  content_name?: string;
  content_category?: string;
  [key: string]: unknown;
}

/**
 * Initialize GTM dataLayer if not already present
 */
export function initializeDataLayer(): void {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    logger.debug(LogCategory.ANALYTICS, 'DataLayer initialized');
  }
}

/**
 * Push event to GTM dataLayer and log to Supabase
 */
export function pushGTMEvent(eventData: GTMEvent): void {
  if (typeof window === 'undefined') {
    return;
  }

  // Ensure dataLayer exists
  if (!window.dataLayer) {
    initializeDataLayer();
  }

  // Add timestamp
  const eventWithTimestamp = {
    ...eventData,
    event_timestamp: new Date().toISOString()
  };

  // Push to GTM dataLayer
  window.dataLayer.push(eventWithTimestamp);

  // Log to Supabase (sanitize enhanced_conversion_data)
  const logData: Record<string, unknown> = { ...eventData };

  // Don't log PII from enhanced_conversion_data
  if (logData.enhanced_conversion_data) {
    logData.enhanced_conversion_data = {
      fields_present: Object.keys(eventData.enhanced_conversion_data || {})
    };
  }

  logger.info(LogCategory.ANALYTICS, `GTM: ${eventData.event}`, logData);
}

/**
 * Fire Facebook Pixel event and log to Supabase
 * Only fires for CreditDirect brand
 */
export function fireFBPixelEvent(
  eventName: string,
  params: FBPixelParams,
  brand: string
): void {
  // Only CreditDirect uses Facebook Pixel
  if (brand !== 'creditdirect') {
    return;
  }

  if (typeof window === 'undefined' || typeof window.fbq !== 'function') {
    logger.debug(LogCategory.ANALYTICS, 'FB Pixel not available', { eventName });
    return;
  }

  try {
    // Fire FB Pixel event
    window.fbq('track', eventName, params);

    // Log to Supabase (sanitize - only log safe fields)
    const safeParams: Record<string, unknown> = {};
    const safeFields = ['currency', 'value', 'content_type', 'content_ids', 'content_name', 'content_category'];

    for (const field of safeFields) {
      if (params[field] !== undefined) {
        safeParams[field] = params[field];
      }
    }

    logger.info(LogCategory.ANALYTICS, `FB Pixel: ${eventName}`, {
      event: eventName,
      params: safeParams,
      brand
    });
  } catch (error) {
    logger.error(LogCategory.ANALYTICS, `FB Pixel error: ${eventName}`, {
      event: eventName,
      error: error instanceof Error ? error.message : 'Unknown error'
    });
  }
}

// ==================== Pre-defined GTM Events ====================

/**
 * Track form start (first field interaction)
 */
export function trackFormStart(sessionId: string, brand: string): void {
  pushGTMEvent({
    event: 'form_start',
    session_id: sessionId,
    brand
  });
}

/**
 * Track form step completion
 */
export function trackFormStepComplete(
  sessionId: string,
  brand: string,
  stepNumber: number,
  fieldsCompleted: number
): void {
  pushGTMEvent({
    event: 'form_step_complete',
    session_id: sessionId,
    brand,
    step_number: stepNumber,
    fields_completed: fieldsCompleted
  });
}

/**
 * Track validation error
 */
export function trackValidationError(
  sessionId: string,
  brand: string,
  fieldName: string,
  errorType: string
): void {
  pushGTMEvent({
    event: 'form_validation_error',
    session_id: sessionId,
    brand,
    field_name: fieldName,
    error_type: errorType
  });
}

/**
 * Track form submission
 */
export function trackFormSubmission(
  sessionId: string,
  brand: string,
  enhancedConversionData?: Record<string, unknown>
): void {
  pushGTMEvent({
    event: 'form_submission',
    session_id: sessionId,
    brand,
    enhanced_conversion_data: enhancedConversionData
  });
}

/**
 * Track credit decision outcome
 */
export function trackCreditDecision(
  sessionId: string,
  brand: string,
  scenarioCode: number,
  outcomeType: string
): void {
  pushGTMEvent({
    event: 'credit_decision',
    session_id: sessionId,
    brand,
    scenario_code: scenarioCode,
    outcome_type: outcomeType
  });
}

/**
 * Track conversion event
 */
export function trackConversion(
  sessionId: string,
  brand: string,
  conversionType: string,
  value?: number
): void {
  pushGTMEvent({
    event: 'conversion',
    session_id: sessionId,
    brand,
    conversion_type: conversionType,
    conversion_value: value
  });
}

/**
 * Track outcome button click - Supabase logging ONLY (not sent to GTM/Google)
 * This is internal click tracking, not a conversion event
 */
export function trackOutcomeButtonClick(
  sessionId: string,
  brand: string,
  buttonType: string,
  outcomeType: string,
  scenarioCode?: number,
  targetUrl?: string,
  urlStatus?: string
): void {
  // Log to Supabase ONLY - do NOT push to GTM dataLayer
  // This is click tracking for internal analytics, not a Google conversion event
  logger.info(LogCategory.ANALYTICS, 'Click Tracking: outcome_button_click', {
    event: 'outcome_button_click',
    session_id: sessionId,
    brand,
    button_type: buttonType,
    outcome_type: outcomeType,
    scenario_code: scenarioCode,
    target_url: targetUrl ? targetUrl.substring(0, 200) : undefined,
    url_status: urlStatus // 'valid_url', 'invalid_protocol', 'malformed_url', or 'no_url'
  });
}

// ==================== Pre-defined FB Pixel Events ====================

/**
 * Track FB ViewContent (page load)
 */
export function trackFBViewContent(brand: string, contentName?: string): void {
  fireFBPixelEvent('ViewContent', {
    content_name: contentName || 'Lead Form',
    content_type: 'product'
  }, brand);
}

/**
 * Track FB Contact (contact info entered)
 */
export function trackFBContact(brand: string): void {
  fireFBPixelEvent('Contact', {
    content_category: 'lead_form'
  }, brand);
}

/**
 * Track FB Lead (form completed)
 */
export function trackFBLead(brand: string, value?: number): void {
  fireFBPixelEvent('Lead', {
    currency: 'USD',
    value: value || 0
  }, brand);
}

/**
 * Track FB CompleteRegistration (successful outcome)
 */
export function trackFBCompleteRegistration(brand: string, value?: number): void {
  fireFBPixelEvent('CompleteRegistration', {
    currency: 'USD',
    value: value || 0,
    content_type: 'loan_application'
  }, brand);
}

export default {
  initializeDataLayer,
  pushGTMEvent,
  fireFBPixelEvent,
  trackFormStart,
  trackFormStepComplete,
  trackValidationError,
  trackFormSubmission,
  trackCreditDecision,
  trackConversion,
  trackOutcomeButtonClick,
  trackFBViewContent,
  trackFBContact,
  trackFBLead,
  trackFBCompleteRegistration
};
